import { call, delay, put, take, takeEvery } from 'redux-saga/effects';
import { getType } from 'deox';
import {
  addProjectFile,
  applyProject,
  createNewProjectFile,
  createProjectFile,
  deleteStoredProjectFile,
  getCurrentProjectId,
  getProjectById,
  getProjectFilesList,
  getStoreProjectFilesList,
  replaceProjectFile,
  saveProjectFile,
  selectProjectFile,
  setCurrentProjectFile,
  setProjectFileList,
  setStatus,
  updateStoredProjectFile
} from 'modules/projectFles';
import { isProjectData, Migration, ProjectData, ProjectFileBase } from 'types/ProjectFiles';
import { addAlert } from 'modules/alert';
import { select } from 'redux-saga-test-plan/matchers';
import { defaultScenesState, getScenes } from 'modules/scenes';
import { Scene, SceneType } from 'types/Scene';
import { nanoid } from 'nanoid/non-secure';
import { getUser } from 'modules/user';
import { ActiveTool, AppState, ToolName } from 'types/AppState';
import { getRatioFormat } from 'modules/ratioFormat';
import { loadEffectsFont } from './effectsSaga';
import { hasUrlParametr } from 'utils/utils';
import { removeURLParameters } from 'utils/directLinks';
import { getPreview } from 'utils/projects/projectPreview';
import { isNil } from 'ramda';
import { directLinkToTool, getActiveTool } from 'modules/activeTool';
import { initScenesLoader } from 'modules/scenesLoader';
import { projectMessages } from 'constants/errorMessages';
import {
  defaultRevisionsState,
  getRevisions,
  getRevisionsOffset,
  getSnapshot,
  saveRevision,
  updateRevisions
} from 'modules/revisions';
import { Revision, Revisions, Snapshot } from 'types/Revision';
import { RatioFormatType } from 'types/RatioFormat';
import { User } from 'types/User';
import { defaultUserSongs } from 'modules/userSongs';
import { STORAGE_KEY, VERSION } from 'constants/projects';
import { getCheckoutModal } from 'modules/checkoutModal';
import { getPlayer } from 'utils/PlayerContext';
import { getDownloadingState } from 'modules/downloading';
import ErrorLogger from '../../components/ErrorLogger';

const saveProjectFileList = (userId: number, list: ProjectFileBase[]): void => {
  localStorage.setItem(`${STORAGE_KEY}${userId}`, JSON.stringify(list));
};

const projectFileGetList = (id: number): ProjectFileBase[] => {
  return JSON.parse(localStorage.getItem(`${STORAGE_KEY}${id}`)) || [];
};

function* updateProjectEffects(projectData: ProjectData) {
  for (const key in projectData.effects) {
    if (projectData.effects[key] !== undefined) {
      yield call(loadEffectsFont, projectData.effects[key]);
    }
  }
}

const getMigration = async (version: number): Promise<Migration> => {
  const migrationModule = await import(`../projectMigrations/${version}`);
  return migrationModule.default;
};

const projectFileCreate = (
  userId: number,
  projectFile: Omit<ProjectFileBase, 'id'> & Partial<AppState>
): string => {
  const id = nanoid();
  const pF = {
    ...projectFile,
    id
  };
  const projectFileList = projectFileGetList(userId);
  projectFileList.push(pF);
  saveProjectFileList(userId, projectFileList);
  return id;
};

const projectFileDelete = (userId: number, projectId: string) => {
  const projectFileList = projectFileGetList(userId);
  const newList = projectFileList.filter(pf => pf.id !== projectId);
  saveProjectFileList(userId, newList);
};

const projectFileUpdate = (
  userId: number,
  projectId: string,
  data: ProjectFileBase & Partial<AppState>
) => {
  const projectFileList = projectFileGetList(userId);
  const newList = projectFileList.map(pf => (pf.id === projectId ? { ...pf, ...data } : pf));
  saveProjectFileList(userId, newList);
};

const projectFileGetProjectById = (userId: number, id: string): ProjectFileBase | null => {
  const projectFileList = projectFileGetList(userId);
  return projectFileList.find(item => item.id === id);
};

export function* getProjectFileData(): Generator<any, ProjectData, any> {
  const revisions: Revision[] = yield select(getRevisions);
  const revisionOffset: Revisions['offset'] = yield select(getRevisionsOffset);

  const snapshot: Snapshot = yield select(getSnapshot);
  const { projectName, ...projectData } = snapshot;

  const checkoutModal = yield select(getCheckoutModal);
  const downloading = yield select(getDownloadingState);

  return {
    ...projectData,
    checkoutModal,
    downloading,
    revisions: {
      items: revisions,
      offset: revisionOffset
    }
  };
}

export function* createNewProjectFileSaga() {
  yield saveProjectSaga();
  const projectFileList: ProjectFileBase[] = yield select(getStoreProjectFilesList);
  if (projectFileList.length >= 3) {
    yield put(replaceProjectFile());
  } else {
    yield put(createProjectFile());
  }
  yield put(saveRevision(ToolName.None));
}

export function* getProjectFilesListSaga({ payload }: ReturnType<typeof getProjectFilesList>) {
  try {
    const projectFileList: ProjectFileBase[] = yield call(projectFileGetList, payload);
    yield put(setProjectFileList(projectFileList));
    const sortedList = [...projectFileList].sort((a, b) => b.date - a.date);
    const lastProjectId = sortedList[0]?.id;
    const isCreate = hasUrlParametr('create');

    if (isCreate || isNil(lastProjectId)) {
      yield put(createNewProjectFile());
    }

    if (isCreate) {
      removeURLParameters(['create']);
      yield put(directLinkToTool());
      return;
    }

    if (lastProjectId) {
      yield put(selectProjectFile(lastProjectId));
    }
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(projectMessages.title, projectMessages.list));
  }
}

export function* getProjectPreview() {
  const scenes: Scene[] = yield select(getScenes);
  const firstScene = scenes[0];
  if (isNil(firstScene.src)) {
    return null;
  }
  const preview =
    firstScene.type !== SceneType.EMPTY
      ? // @ts-ignore
        yield call(getPreview, firstScene.src, firstScene.type)
      : '';

  return preview;
}

export function* createProjectFileSaga() {
  try {
    yield call(dropAppState);
    yield put(setCurrentProjectFile(null));
    yield take(getType(saveProjectFile));
    const snapshot: Snapshot = yield select(getSnapshot);
    const name = snapshot.projectName;
    const src: string = yield call(getProjectPreview);
    const { id: userId } = yield select(getUser);
    const date = Date.now();
    const data: Partial<AppState> = yield call(getProjectFileData);
    const projectId: string = yield call<any>(projectFileCreate, userId, {
      src,
      date,
      name,
      version: VERSION,
      ...data
    });

    const project = { src, date, name, id: projectId };

    yield put(addProjectFile(project));
    yield put(setCurrentProjectFile(project.id));
    yield put(updateRevisions(defaultRevisionsState));
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(projectMessages.title, projectMessages.create));
  }
}

export function* dropAppState() {
  const ratioFormat: RatioFormatType = yield select(getRatioFormat);
  const activeTool: ActiveTool = yield select(getActiveTool);

  const defaultState: ProjectData = {
    activeTool,
    ratioFormat,
    effects: {},
    scenes: defaultScenesState,
    audio: null,
    sceneVolume: {},
    revisions: defaultRevisionsState,
    userSongs: defaultUserSongs
  };
  yield put(applyProject(defaultState));
}

export function* replaceProjectFileSaga() {
  try {
    const { id: userId } = yield select(getUser);
    const list: ProjectFileBase[] = yield select(getStoreProjectFilesList);
    const sortedList = [...list].sort((a, b) => a.date - b.date);
    const oldProject = sortedList[0];
    yield call(projectFileDelete, userId, oldProject.id);
    yield put(deleteStoredProjectFile(oldProject.id));
    yield put(createProjectFile());
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(projectMessages.title, projectMessages.create));
  }
}

export function* saveProjectSaga() {
  try {
    const user: User = yield select(getUser);
    if (!user) {
      return;
    }
    yield put(setStatus('saving'));
    const data: Partial<AppState> = yield call(getProjectFileData);
    const src: string = yield call(getProjectPreview);
    const id: string = yield select(getCurrentProjectId);
    const project: Partial<AppState> & ProjectFileBase = {
      ...(yield select(getProjectById(id))),
      src,
      date: Date.now()
    };
    yield call(projectFileUpdate, user.id, id, { ...project, ...data });
    yield put(updateStoredProjectFile(id, project));
    yield delay(100);
    yield put(setStatus('saved'));
  } catch (error) {
    ErrorLogger.trigger(error);
    yield put(addAlert(projectMessages.title, projectMessages.update));
  }
}

function* selectProjectFileSaga({ payload }: ReturnType<typeof selectProjectFile>) {
  const { id: userId } = yield select(getUser);
  const projectFile: ProjectFileBase | null = yield call(
    projectFileGetProjectById,
    userId,
    payload
  );

  if (projectFile === null) {
    yield put(addAlert(projectMessages.title, projectMessages.update));
    return;
  }

  const { id, name, src, date, ...rest } = projectFile;

  let { version, ...projectData } = rest;

  while (version < VERSION) {
    version += 1;
    const migrate: Migration = yield call(getMigration, version);
    projectData = yield call(migrate, projectData, projectFile);
  }

  if (!isProjectData(projectData)) {
    yield put(addAlert(projectMessages.title, projectMessages.update));
    return;
  }

  yield call(updateProjectEffects, projectData);

  getPlayer().pause();
  yield put(applyProject(projectData));
  yield put(initScenesLoader());
  yield put(setCurrentProjectFile(id));

  if (isNil(projectData?.revisions)) {
    yield put(saveRevision(ToolName.None));
    yield put(updateRevisions({ offset: 0 }));
  }

  yield put(saveProjectFile());
}

export default function* projectFilesSaga() {
  yield takeEvery(getType(getProjectFilesList), getProjectFilesListSaga);
  yield takeEvery(getType(createProjectFile), createProjectFileSaga);
  yield takeEvery(getType(replaceProjectFile), replaceProjectFileSaga);
  yield takeEvery(getType(selectProjectFile), selectProjectFileSaga);
  yield takeEvery(getType(createNewProjectFile), createNewProjectFileSaga);
  yield takeEvery(getType(saveProjectFile), saveProjectSaga);
}
